package com.fantasytenis.web.components.bracket

import androidx.compose.runtime.*
import com.fantasytenis.shared.domain.bracket.entity.BracketMatchEntity
import com.fantasytenis.shared.domain.bracket.entity.DrawPositionEntity
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.borderTop
import com.fantasytenis.web.theme.borderBottom
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Match node variant for different display contexts.
 */
enum class MatchNodeVariant {
    FULL,      // Full details (name, seed, country)
    COMPACT,   // Name and seed only
    MINI       // Seed or abbreviated name only (for bird's eye view)
}

/**
 * State of a match node for styling purposes.
 */
sealed class MatchNodeState {
    object Default : MatchNodeState()      // No pick made yet
    object Picked : MatchNodeState()       // User has made a pick
    object Correct : MatchNodeState()      // Pick was correct (live scoring)
    object Incorrect : MatchNodeState()    // Pick was incorrect (live scoring)
    object ViewOnly : MatchNodeState()     // Read-only mode
}

/**
 * A single match node in the bracket tree.
 *
 * Displays two players with the ability to click to select a winner.
 * The selected player visually "advances" to the next round.
 */
/**
 * Display data for a player that can come from either draw data or user picks.
 */
data class PlayerDisplayData(
    val name: String,
    val seed: Int?,
    val country: String?,
    val playerId: String?,
    val isFromPick: Boolean = false,
    val potentialPlayers: List<String>? = null, // Players who could advance here (when no pick made)
    /** Section number (1-indexed) that this player's pick comes from, if the slot is TBD */
    val feederSectionNumber: Int? = null
) {
    val hasPlayer: Boolean get() = playerId != null
    val displayName: String get() = name
    val hasPotentialPlayers: Boolean get() = !potentialPlayers.isNullOrEmpty()
    /** True if this is a TBD slot that has a feeder section the user can click to complete */
    val hasFeederSection: Boolean get() = !hasPlayer && feederSectionNumber != null
}

/**
 * Create PlayerDisplayData from a DrawPositionEntity.
 */
fun DrawPositionEntity.toDisplayData(): PlayerDisplayData {
    return PlayerDisplayData(
        name = displayName,
        seed = seed,
        country = playerCountry,
        playerId = playerId,
        isFromPick = false
    )
}

@Composable
fun BracketMatchNode(
    match: BracketMatchEntity,
    selectedWinnerId: String?,
    isViewOnly: Boolean,
    variant: MatchNodeVariant = MatchNodeVariant.FULL,
    width: Int = BracketLayoutConfig.MATCH_WIDTH,
    height: Int = BracketLayoutConfig.MATCH_HEIGHT,
    onSelectWinner: (playerId: String) -> Unit,
    onShowDetails: (() -> Unit)? = null,
    // Optional display data overrides - when provided, use these instead of match.player1/player2
    player1Display: PlayerDisplayData? = null,
    player2Display: PlayerDisplayData? = null,
    /** Callback when user clicks "[Pick in Sec X]" link for a TBD player slot */
    onFeederSectionClick: ((sectionNumber: Int) -> Unit)? = null
) {
    @Suppress("UNUSED_VARIABLE")
    val state = when {
        isViewOnly -> MatchNodeState.ViewOnly
        selectedWinnerId != null -> MatchNodeState.Picked
        else -> MatchNodeState.Default
    }

    // Use provided display data or fall back to match data
    val effectiveP1 = player1Display ?: match.player1.toDisplayData()
    val effectiveP2 = player2Display ?: match.player2.toDisplayData()

    Div({
        classes("bracket-match-node")
        attr("tabindex", "0")
        attr("role", "group")
        attr("aria-label", "Match ${match.matchNumber}: ${effectiveP1.displayName} vs ${effectiveP2.displayName}")
        style {
            property("width", "${width}px")
            property("min-height", "${height}px")  // Use min-height to allow content to grow
            backgroundColor(Colors.Surface)
            borderRadius(8.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            // No overflow:hidden - let content determine actual height
        }
    }) {
        // Match header with round info and points
        if (variant == MatchNodeVariant.FULL) {
            MatchHeader(match = match)
        }

        // Player rows container
        Div({
            style {
                flex(1)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                padding(4.px)
                gap(2.px)
            }
        }) {
            // Player 1
            PlayerRowWithDisplayData(
                displayData = effectiveP1,
                isSelected = selectedWinnerId == effectiveP1.playerId,
                isViewOnly = isViewOnly,
                variant = variant,
                onSelect = {
                    if (!isViewOnly) {
                        effectiveP1.playerId?.let { onSelectWinner(it) }
                    }
                },
                onFeederClick = onFeederSectionClick
            )

            // Player 2
            PlayerRowWithDisplayData(
                displayData = effectiveP2,
                isSelected = selectedWinnerId == effectiveP2.playerId,
                isViewOnly = isViewOnly,
                variant = variant,
                onSelect = {
                    if (!isViewOnly) {
                        effectiveP2.playerId?.let { onSelectWinner(it) }
                    }
                },
                onFeederClick = onFeederSectionClick
            )
        }

        // Details link (only in full variant)
        if (variant == MatchNodeVariant.FULL && onShowDetails != null) {
            Div({
                style {
                    borderTop(1.px, LineStyle.Solid, Colors.BorderLight)
                    padding(4.px)
                    textAlign("center")
                }
            }) {
                Span({
                    style {
                        color(Colors.Primary)
                        fontSize(11.px)
                        cursor("pointer")
                    }
                    onClick { onShowDetails() }
                }) {
                    Text("Compare")
                }
            }
        }
    }
}

@Composable
private fun MatchHeader(match: BracketMatchEntity) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            padding(4.px, 8.px)
            backgroundColor(Colors.Gray50)
            borderBottom(1.px, LineStyle.Solid, Colors.BorderLight)
        }
    }) {
        // Match number
        Span({
            style {
                fontSize(10.px)
                color(Colors.TextSecondary)
                fontWeight(500)
            }
        }) {
            Text("M${match.matchNumber}")
        }

        // Points badge
        Span({
            style {
                backgroundColor(Colors.Secondary)
                color(Colors.Black)
                fontSize(9.px)
                fontWeight(700)
                padding(2.px, 6.px)
                borderRadius(4.px)
            }
        }) {
            Text("${match.round.basePoints}pt")
        }
    }
}

@Composable
private fun PlayerRow(
    position: DrawPositionEntity,
    isSelected: Boolean,
    isViewOnly: Boolean,
    variant: MatchNodeVariant,
    onSelect: () -> Unit
) {
    val hasPlayer = position.playerId != null
    val isClickable = hasPlayer && !isViewOnly

    Div({
        classes("bracket-player-row")
        if (isSelected) classes("selected")
        if (isViewOnly) classes("view-only")
        if (isClickable) {
            attr("tabindex", "0")
            attr("role", "button")
            attr("aria-pressed", isSelected.toString())
            attr("aria-label", "Pick ${position.displayName}${if (position.seed != null) " (Seed ${position.seed})" else ""}")
        }
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            padding(if (variant == MatchNodeVariant.MINI) 2.px else 6.px)
            borderRadius(4.px)
            cursor(if (isClickable) "pointer" else "default")
            backgroundColor(
                when {
                    isSelected -> Colors.SuccessLight
                    else -> Colors.SurfaceVariant
                }
            )
            border(
                if (variant == MatchNodeVariant.MINI) 1.px else 2.px,
                LineStyle.Solid,
                if (isSelected) Colors.Success else Color("transparent")
            )
            flex(1)
            if (isViewOnly) {
                opacity(0.85)
            }
        }
        if (isClickable) {
            onClick { onSelect() }
            // Keyboard support - Enter or Space to select
            onKeyDown { event ->
                if (event.key == "Enter" || event.key == " ") {
                    event.preventDefault()
                    onSelect()
                }
            }
        }
    }) {
        when (variant) {
            MatchNodeVariant.MINI -> MiniPlayerContent(position, isSelected)
            MatchNodeVariant.COMPACT -> CompactPlayerContent(position, isSelected)
            MatchNodeVariant.FULL -> FullPlayerContent(position, isSelected)
        }
    }
}

/**
 * Player row variant that works with PlayerDisplayData instead of DrawPositionEntity.
 * Used for displaying picked winners in later rounds.
 */
@Composable
private fun PlayerRowWithDisplayData(
    displayData: PlayerDisplayData,
    isSelected: Boolean,
    isViewOnly: Boolean,
    variant: MatchNodeVariant,
    onSelect: () -> Unit,
    onFeederClick: ((sectionNumber: Int) -> Unit)? = null
) {
    val hasPlayer = displayData.hasPlayer
    val isClickable = hasPlayer && !isViewOnly
    val hasClickableFeeder = displayData.hasFeederSection && onFeederClick != null && !isViewOnly

    Div({
        classes("bracket-player-row")
        if (isSelected) classes("selected")
        if (isViewOnly) classes("view-only")
        if (displayData.isFromPick) classes("from-pick") // Visual indicator for picked players
        if (isClickable) {
            attr("tabindex", "0")
            attr("role", "button")
            attr("aria-pressed", isSelected.toString())
            attr("aria-label", "Pick ${displayData.displayName}${if (displayData.seed != null) " (Seed ${displayData.seed})" else ""}")
        }
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            padding(if (variant == MatchNodeVariant.MINI) 2.px else 6.px)
            borderRadius(4.px)
            cursor(if (isClickable || hasClickableFeeder) "pointer" else "default")
            backgroundColor(
                when {
                    isSelected -> Colors.SuccessLight
                    displayData.isFromPick -> Colors.PrimaryLight // Subtle highlight for picked players
                    else -> Colors.SurfaceVariant
                }
            )
            border(
                if (variant == MatchNodeVariant.MINI) 1.px else 2.px,
                if (displayData.isFromPick && !isSelected) LineStyle.Dashed else LineStyle.Solid,
                when {
                    isSelected -> Colors.Success
                    displayData.isFromPick -> Colors.Primary
                    else -> Color("transparent")
                }
            )
            flex(1)
            if (isViewOnly) {
                opacity(0.85)
            }
        }
        if (isClickable) {
            onClick { onSelect() }
            onKeyDown { event ->
                if (event.key == "Enter" || event.key == " ") {
                    event.preventDefault()
                    onSelect()
                }
            }
        }
    }) {
        when (variant) {
            MatchNodeVariant.MINI -> MiniPlayerContentFromData(displayData, isSelected, hasClickableFeeder, onFeederClick)
            MatchNodeVariant.COMPACT -> CompactPlayerContentFromData(displayData, isSelected, hasClickableFeeder, onFeederClick)
            MatchNodeVariant.FULL -> FullPlayerContentFromData(displayData, isSelected, hasClickableFeeder, onFeederClick)
        }
    }
}

@Composable
private fun MiniPlayerContentFromData(
    displayData: PlayerDisplayData,
    isSelected: Boolean,
    hasClickableFeeder: Boolean,
    onFeederClick: ((sectionNumber: Int) -> Unit)?
) {
    if (hasClickableFeeder && displayData.feederSectionNumber != null) {
        // Show clickable section link instead of TBD
        Span({
            style {
                fontSize(9.px)
                fontWeight(500)
                color(Colors.Accent)
                cursor("pointer")
                property("text-decoration", "underline")
            }
            onClick {
                it.stopPropagation()
                onFeederClick?.invoke(displayData.feederSectionNumber)
            }
        }) {
            Text("Sec ${displayData.feederSectionNumber}")
        }
    } else {
        Span({
            style {
                fontSize(10.px)
                fontWeight(if (isSelected) 700 else 500)
                color(if (isSelected) Colors.SuccessDark else Colors.TextPrimary)
                property("white-space", "nowrap")
                overflow("hidden")
                property("text-overflow", "ellipsis")
            }
            // Show potential players as tooltip for TBD entries
            if (displayData.hasPotentialPlayers) {
                attr("title", displayData.potentialPlayers!!.joinToString(" / "))
            }
        }) {
            // Only show seed if > 0, otherwise show abbreviated name
            val display = displayData.seed?.takeIf { it > 0 }?.let { "[$it]" } ?: displayData.displayName.take(6)
            Text(display)
        }
    }
}

@Composable
private fun CompactPlayerContentFromData(
    displayData: PlayerDisplayData,
    isSelected: Boolean,
    hasClickableFeeder: Boolean,
    onFeederClick: ((sectionNumber: Int) -> Unit)?
) {
    // Only show seed badge when seed > 0
    displayData.seed?.takeIf { it > 0 }?.let { seed ->
        Span({
            style {
                backgroundColor(Colors.Primary)
                color(Colors.White)
                fontSize(9.px)
                fontWeight(600)
                padding(1.px, 4.px)
                borderRadius(3.px)
                marginRight(6.px)
            }
        }) {
            Text("$seed")
        }
    }

    // Name and potential players (or clickable feeder link)
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            flex(1)
            overflow("hidden")
        }
    }) {
        if (hasClickableFeeder && displayData.feederSectionNumber != null) {
            // Show clickable "[Pick in Sec X]" link for TBD slots
            Span({
                style {
                    fontSize(11.px)
                    fontWeight(500)
                    color(Colors.Accent)
                    cursor("pointer")
                    property("text-decoration", "underline")
                }
                onClick {
                    it.stopPropagation()
                    onFeederClick?.invoke(displayData.feederSectionNumber)
                }
            }) {
                Text("[Pick in Sec ${displayData.feederSectionNumber}]")
            }
        } else {
            Span({
                style {
                    fontSize(11.px)
                    fontWeight(if (isSelected) 600 else 400)
                    color(Colors.TextPrimary)
                    property("white-space", "nowrap")
                    overflow("hidden")
                    property("text-overflow", "ellipsis")
                }
            }) {
                Text(displayData.displayName)
            }

            // Show potential players for TBD entries
            if (displayData.hasPotentialPlayers) {
                Span({
                    style {
                        fontSize(9.px)
                        color(Colors.TextSecondary)
                        property("white-space", "nowrap")
                        overflow("hidden")
                        property("text-overflow", "ellipsis")
                    }
                }) {
                    Text(displayData.potentialPlayers!!.joinToString(" / "))
                }
            }
        }
    }

    // Show "picked" indicator for players advancing from picks
    if (displayData.isFromPick && !isSelected) {
        Span({
            style {
                fontSize(8.px)
                color(Colors.Primary)
                marginLeft(4.px)
            }
        }) {
            Text("\u2191") // Up arrow to indicate advancement
        }
    }

    if (isSelected) {
        SelectionCheckmark(size = 16)
    }
}

@Composable
private fun FullPlayerContentFromData(
    displayData: PlayerDisplayData,
    isSelected: Boolean,
    hasClickableFeeder: Boolean,
    onFeederClick: ((sectionNumber: Int) -> Unit)?
) {
    // Seed badge - only show when seed > 0
    displayData.seed?.takeIf { it > 0 }?.let { seed ->
        Span({
            style {
                backgroundColor(Colors.Primary)
                color(Colors.White)
                fontSize(10.px)
                fontWeight(600)
                padding(2.px, 5.px)
                borderRadius(3.px)
                marginRight(8.px)
                flexShrink(0)
            }
        }) {
            Text("$seed")
        }
    }

    // Player info
    Div({
        style {
            flex(1)
            overflow("hidden")
        }
    }) {
        if (hasClickableFeeder && displayData.feederSectionNumber != null) {
            // Show clickable "[Pick in Sec X]" link for TBD slots
            Span({
                style {
                    fontSize(12.px)
                    fontWeight(500)
                    color(Colors.Accent)
                    cursor("pointer")
                    property("text-decoration", "underline")
                }
                onClick {
                    it.stopPropagation()
                    onFeederClick?.invoke(displayData.feederSectionNumber)
                }
            }) {
                Text("[Pick in Sec ${displayData.feederSectionNumber}]")
            }
            // Show potential players below the link
            if (displayData.hasPotentialPlayers) {
                Div({
                    style {
                        fontSize(10.px)
                        color(Colors.TextSecondary)
                        property("white-space", "nowrap")
                        overflow("hidden")
                        property("text-overflow", "ellipsis")
                    }
                }) {
                    Text(displayData.potentialPlayers!!.joinToString(" / "))
                }
            }
        } else {
            // Name row with optional pick indicator
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(4.px)
                }
            }) {
                Span({
                    style {
                        fontSize(12.px)
                        fontWeight(if (isSelected) 600 else 500)
                        color(Colors.TextPrimary)
                        property("white-space", "nowrap")
                        overflow("hidden")
                        property("text-overflow", "ellipsis")
                    }
                }) {
                    Text(displayData.displayName)
                }

                // Show advancement indicator for picked players
                if (displayData.isFromPick && !isSelected) {
                    Span({
                        style {
                            fontSize(9.px)
                            color(Colors.Primary)
                            fontWeight(500)
                        }
                    }) {
                        Text("(adv.)")
                    }
                }
            }

            // Show potential players (when no pick made yet but we know the feeder match players)
            if (displayData.hasPotentialPlayers) {
                Div({
                    style {
                        fontSize(10.px)
                        color(Colors.TextSecondary)
                        property("white-space", "nowrap")
                        overflow("hidden")
                        property("text-overflow", "ellipsis")
                    }
                }) {
                    Text(displayData.potentialPlayers!!.joinToString(" / "))
                }
            } else {
                // Country (if available)
                displayData.country?.let { country ->
                    Div({
                        style {
                            fontSize(10.px)
                            color(Colors.TextSecondary)
                        }
                    }) {
                        Text(country)
                    }
                }
            }
        }
    }

    if (isSelected) {
        SelectionCheckmark(size = 20)
    }
}

@Composable
private fun MiniPlayerContent(position: DrawPositionEntity, isSelected: Boolean) {
    // Just seed (if > 0) or first few letters of name
    Span({
        style {
            fontSize(10.px)
            fontWeight(if (isSelected) 700 else 500)
            color(if (isSelected) Colors.SuccessDark else Colors.TextPrimary)
            property("white-space", "nowrap")
            overflow("hidden")
            property("text-overflow", "ellipsis")
        }
    }) {
        val display = position.seed?.takeIf { it > 0 }?.let { "[$it]" } ?: position.displayName.take(6)
        Text(display)
    }
}

@Composable
private fun CompactPlayerContent(position: DrawPositionEntity, isSelected: Boolean) {
    // Seed badge + name (only show seed if > 0)
    position.seed?.takeIf { it > 0 }?.let { seed ->
        Span({
            style {
                backgroundColor(Colors.Primary)
                color(Colors.White)
                fontSize(9.px)
                fontWeight(600)
                padding(1.px, 4.px)
                borderRadius(3.px)
                marginRight(6.px)
            }
        }) {
            Text("$seed")
        }
    }

    Span({
        style {
            fontSize(11.px)
            fontWeight(if (isSelected) 600 else 400)
            color(Colors.TextPrimary)
            property("white-space", "nowrap")
            overflow("hidden")
            property("text-overflow", "ellipsis")
            flex(1)
        }
    }) {
        Text(position.displayName)
    }

    // Selection indicator
    if (isSelected) {
        SelectionCheckmark(size = 16)
    }
}

@Composable
private fun FullPlayerContent(position: DrawPositionEntity, isSelected: Boolean) {
    // Seed badge - only show when seed > 0
    position.seed?.takeIf { it > 0 }?.let { seed ->
        Span({
            style {
                backgroundColor(Colors.Primary)
                color(Colors.White)
                fontSize(10.px)
                fontWeight(600)
                padding(2.px, 5.px)
                borderRadius(3.px)
                marginRight(8.px)
                flexShrink(0)
            }
        }) {
            Text("$seed")
        }
    }

    // Player info
    Div({
        style {
            flex(1)
            overflow("hidden")
        }
    }) {
        // Name
        Div({
            style {
                fontSize(12.px)
                fontWeight(if (isSelected) 600 else 500)
                color(Colors.TextPrimary)
                property("white-space", "nowrap")
                overflow("hidden")
                property("text-overflow", "ellipsis")
            }
        }) {
            Text(position.displayName)
        }

        // Country (if available)
        position.playerCountry?.let { country ->
            Div({
                style {
                    fontSize(10.px)
                    color(Colors.TextSecondary)
                }
            }) {
                Text(country)
            }
        }
    }

    // Selection indicator
    if (isSelected) {
        SelectionCheckmark(size = 20)
    }
}

@Composable
private fun SelectionCheckmark(size: Int) {
    Div({
        classes("pick-checkmark")
        attr("aria-hidden", "true")
        style {
            property("width", "${size}px")
            property("height", "${size}px")
            backgroundColor(Colors.Success)
            borderRadius(50.percent)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            flexShrink(0)
            marginLeft(4.px)
        }
    }) {
        Span({
            style {
                color(Colors.White)
                fontSize((size * 0.6).px)
                fontWeight(700)
            }
        }) {
            Text("\u2713") // Checkmark
        }
    }
}

/**
 * Displays the winner that advances to the next round.
 * This is shown in the next round's match slot before opponents are determined.
 */
@Composable
fun AdvancingPlayerSlot(
    playerName: String?,
    seed: Int?,
    isPicked: Boolean,
    width: Int = BracketLayoutConfig.MATCH_WIDTH
) {
    Div({
        style {
            property("width", "${width}px")
            property("min-height", "32px")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            padding(6.px, 8.px)
            borderRadius(4.px)
            backgroundColor(if (isPicked) Colors.SuccessLight else Colors.SurfaceVariant)
            border(
                1.px,
                if (isPicked) LineStyle.Solid else LineStyle.Dashed,
                if (isPicked) Colors.Success else Colors.Border
            )
        }
    }) {
        if (playerName != null) {
            seed?.let { s ->
                Span({
                    style {
                        backgroundColor(Colors.Primary)
                        color(Colors.White)
                        fontSize(9.px)
                        fontWeight(600)
                        padding(1.px, 4.px)
                        borderRadius(3.px)
                        marginRight(6.px)
                    }
                }) {
                    Text("$s")
                }
            }

            Span({
                style {
                    fontSize(11.px)
                    fontWeight(500)
                    color(Colors.TextPrimary)
                }
            }) {
                Text(playerName)
            }
        } else {
            Span({
                style {
                    fontSize(11.px)
                    color(Colors.TextSecondary)
                    fontStyle("italic")
                }
            }) {
                Text("TBD")
            }
        }
    }
}
